import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

function fmtDateRu(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
function age(iso) {
  if (!iso) return null;
  const b = new Date(iso); const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a;
}

/**
 * Зачисление ученика в класс. Список кандидатов берётся с бэка
 * /api/classes/{id}/eligible-students — там фильтрация по договору и статусу.
 *
 * onEnrolled() — родитель должен перезагрузить список учеников класса.
 */
export default function EnrollStudentModal({ cls, onClose, onEnrolled }) {
  const [q, setQ]           = useState('');
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null); // id строки, у которой раскрыты поля дат
  const [dateStart, setDateStart] = useState('');
  const [dateEnd,   setDateEnd]   = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  // Поиск с дебаунсом — ходим на бэк, фильтр там же.
  useEffect(() => {
    let aborted = false;
    setLoading(true); setError(null);
    const t = setTimeout(() => {
      const qs = q.trim() ? '?q=' + encodeURIComponent(q.trim()) : '';
      api.get('/api/classes/' + cls.id + '/eligible-students' + qs)
        .then((res) => { if (!aborted) setRows(res.rows || []); })
        .catch((e)  => { if (!aborted) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
        .finally(() => { if (!aborted) setLoading(false); });
    }, 200);
    return () => { aborted = true; clearTimeout(t); };
  }, [q, cls.id]);

  const startEdit = (s) => {
    setEditingId(s.id);
    setDateStart(s.agreement.dateStart || '');
    setDateEnd(s.agreement.dateEnd || '');
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDateStart(''); setDateEnd('');
  };

  const enroll = async (s) => {
    setSaving(true); setSavingId(s.id); setError(null);
    try {
      await api.post('/api/classes/' + cls.id + '/students', {
        studentId: s.id,
        dateStart: dateStart || s.agreement.dateStart,
        dateEnd:   dateEnd   || s.agreement.dateEnd,
      });
      onEnrolled?.();
      setRows((prev) => prev.filter((r) => r.id !== s.id));
      setEditingId(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось зачислить'));
    } finally {
      setSaving(false); setSavingId(null);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true" style={{ maxWidth: 720 }}>
        <div className="sd-edit__head">
          <h2>Зачислить в класс — {cls.code}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>

        <div style={{ padding: '12px 20px 0' }}>
          <div className="cls-toolbar__search" style={{ width: '100%' }}>
            <Icon name="search" size={13} />
            <input
              placeholder="Поиск по ФИО ученика…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
            {q && <button onClick={() => setQ('')}><Icon name="x" size={11} /></button>}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 8 }}>
            Показаны ученики со статусом «активен» и подписанным договором за этот учебный год
            {cls.stage === 'primary' ? ` на ${cls.number} класс` : ' на детский сад'}.
          </div>
        </div>

        <div style={{ padding: '12px 20px 16px', maxHeight: 480, overflowY: 'auto' }}>
          {error && <div className="sd-edit__error" style={{ marginBottom: 10 }}>{error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">
              {q ? 'Никого не найдено по запросу.' : 'Нет кандидатов на зачисление в этот класс.'}
            </div>
          ) : (
            <div className="enroll-list">
              {rows.map((s) => {
                const fio = `${s.familyName} ${s.firstName}${s.midName ? ' ' + s.midName : ''}`;
                const a = age(s.birthdate);
                const isEditing = editingId === s.id;
                const agStart = s.agreement.dateStart;
                const agEnd   = s.agreement.dateEnd;
                return (
                  <div key={s.id} className="enroll-row">
                    <div className="enroll-row__top">
                      <div className="enroll-row__main">
                        <div className="enroll-row__name">{fio}</div>
                        <div className="enroll-row__meta">
                          <span className="mono">{fmtDateRu(s.birthdate)}</span>
                          {a !== null && <span> · {a} лет</span>}
                          <span> · договор № {s.agreement.number}</span>
                          {agStart && agEnd && (
                            <span style={{ color: 'var(--fg-faint)' }}> · {fmtDateRu(agStart)} — {fmtDateRu(agEnd)}</span>
                          )}
                        </div>
                        {s.otherClass && (
                          <div className="enroll-row__warn">
                            <Icon name="alert" size={11} />
                            <span>Период пересекается с классом <b>{s.otherClass.code}</b> (до {fmtDateRu(s.otherClass.until)}) — сначала закройте период.</span>
                          </div>
                        )}
                      </div>
                      {!isEditing && (
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => startEdit(s)}
                          disabled={saving || !!s.otherClass}
                        >
                          Зачислить
                        </button>
                      )}
                    </div>

                    {isEditing && (
                      <div className="enroll-row__form">
                        <div className="enroll-row__field">
                          <label>Дата начала</label>
                          <input
                            type="date"
                            value={dateStart}
                            min={agStart || undefined}
                            max={agEnd || undefined}
                            onChange={(e) => setDateStart(e.target.value)}
                          />
                        </div>
                        <div className="enroll-row__field">
                          <label>Дата окончания</label>
                          <input
                            type="date"
                            value={dateEnd}
                            min={dateStart || agStart || undefined}
                            max={agEnd || undefined}
                            onChange={(e) => setDateEnd(e.target.value)}
                          />
                        </div>
                        <div className="enroll-row__formHint">
                          В пределах действия договора: {fmtDateRu(agStart)} — {fmtDateRu(agEnd)}
                        </div>
                        <div className="enroll-row__formActions">
                          <button type="button" className="btn btn--sm" onClick={cancelEdit} disabled={saving}>Отмена</button>
                          <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            onClick={() => enroll(s)}
                            disabled={saving || !dateStart || !dateEnd}
                          >
                            {savingId === s.id ? 'Зачисляем…' : 'Подтвердить'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
